import { fieldOptionsFactory, IFormSchema } from 'spark-forms';
import { WorkProfileSelector } from '../../../../../../../forms/_builders/new/components/WorkProfileSelector';
import { DropdownOptionEx } from '../../../../../../../Utilities/DropdownOptionEx';
import { ContentConfigurationType, DynamicQuery, EntityType, MailTemplate, QueryDto, SubSettingsOverrides, UnitType, UserType } from '../../../../../../../api/generated/data-contracts';
import EntityFieldFilterEditor from '../../../../../../../forms/_builders/new/components/EntityFieldFilterEditor';
import { settings } from '../../../../../../../context/network/http/QueryProvider/queries/UISettings';
import { resourceSchema } from '../../../../../../../forms/resource/resourceSchema';
import { projectSchema } from '../../../../../../../forms/project/projectSchema';
import { freezePeriodSchema, glimmerConfigurationSchema, pickerFieldsSchema } from './supportSchemas';
import { EntityFieldSelector } from '../../../../../../../forms/_builders/new/components/EntityFieldSelector';
import { ApiCalls } from '../../../../../../../api/api';
import { getTranslationKey } from '../../../../../../../Locales/i18n/utils';
import { translate } from '../../../../../../../Locales/i18n/localizationService';

export const settingsOverridesFieldSchema = (fieldsWhitelist?: string[]) : IFormSchema<SubSettingsOverrides> => {
	const filteredSchema: IFormSchema<SubSettingsOverrides> = { fields: {} };
	fieldsWhitelist.forEach((key) => {
		if (key in schema.fields) {
			filteredSchema.fields[key] = schema.fields[key];
		}
	});

	return filteredSchema;
}

const getLocalizedValue = (key: string) => {
    return translate.t(getTranslationKey(key));
};

export const getSubOverridesSchemaFieldNames = () : { key: string, text: string }[] => {
	return Object.keys(schema.fields).map(key => ({ key, text: getLocalizedValue(schema.fields[key].displayName) }));
}

const schema = {
	fields: {
		workProfileId: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.DefaultWorkProfile"),
			config: { component: WorkProfileSelector },
		}),
		allowSearchAllProjects: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.AllowSearchAllProjects"),
		}),
		allowSearchAllResources: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.AllowSearchAllResources")
		}),
		rbsLevelsBelowAccesibleFor: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.RbsLevelsBelowAccessible"),
			config: {
				predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptionsWithoutZeroOption<UserType>(UserType) },
				multiSelect: true
			},
		}),
		lmCanEditOwnResources: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.LmCanEditOwnResources"),
		}),
		pmCanEditOwnProjects: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.PmCanEditOwnProjects"),
		}),
		shownOwnProjectsForLM: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.ShownOwnProjectsForLM"),
		}),
		glimmerConfiguration: fieldOptionsFactory.formItem({
			displayName: translate.t("Settings.GlimmerConfiguration"),
			config: {
				schema: {
					schema: glimmerConfigurationSchema()
				}
			},
			hideLabel: false
		}),
		gridDecimalsFTE: fieldOptionsFactory.number({
			displayName: translate.t("Settings.GridDecimalsFTE"),
		}),
		gridDecimalsHours: fieldOptionsFactory.number({
			displayName: translate.t("Settings.GridDecimalsHours"),
		}),
		enableAlignContract: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableAlignContract"),
		}),
		enableForwardRequest: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableForwardRequest"),
		}),
		enableMoveRequest: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableMoveRequest"),
		}),
		enableCreateMatchingRequest: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableCreateMatchingRequest"),
		}),
		enableCreateMatchingAllocation: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableCreateMatchingAllocation"),
		}),
		enableMoveAllAllocations: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableMoveAllAllocations"),
		}),
		enableMoveAllResourceRequests: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableMoveAllResourceRequests"),
		}),
		enableMergeContracts: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableMergeContracts"),
		}),
		enableLmCapacityView: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableLmCapacityView"),
		}),
		enableGridUndo: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableGridUndo"),
		}),
		enableCopyFromExcel: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableCopyFromExcel"),
		}),
		enableCoachmarks: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableCoachmarks"),
		}),
		enableRequestNamedResources: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableRequestNamedResource"),
		}),
		enableSearchNamedResources: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableSearchNamedResources"),
		}),
		enableContractVersions: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableContractVersions"),
		}),
		enableMailNotifications: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableMailNotifications"),
		}),
		enableWorkPackages: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableWorkPackages"),
		}),
		resourceFilterPM: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourceFilterPM"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().resourceCustomProperties,
				entityType: 'resource',
				schema: () => resourceSchema,
			}
		}),
		resourceFilterLM: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourceFilterLM"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().resourceCustomProperties,
				entityType: 'resource',
				schema: () => resourceSchema,
			}
		}),
		resourceFilterOverview: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourceFilterOverview"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().resourceCustomProperties,
				entityType: 'resource',
				schema: () => resourceSchema,
			}
		}),
		pmResourcePickerDisplayFields: fieldOptionsFactory.formItem({
			displayName: translate.t("Settings.PMResourcePickerDisplayFields"),
			config: {
				schema: {
					schema: pickerFieldsSchema('resource')
				}
			},
			hideLabel: false
		}),
		lmResourcePickerDisplayFields: fieldOptionsFactory.formItem({
			displayName: translate.t("Settings.LMResourcePickerDisplayFields"),
			config: {
				schema: {
					schema: pickerFieldsSchema('resource')
				}
			},
			hideLabel: false
		}),
		showOnlyRelevantResourcesInPicker: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.ShowOnlyRelevantResourcesInPicker"),
		}),
		resourceInformationProperties: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourceInformationProperties"),
			config: {
				component: EntityFieldSelector,
				schema: () => resourceSchema,
				customProperties: () => settings().resourceCustomProperties,
				multiSelect: true,
				propertyTypes: 'both',
			}
		}),
		allowedToInactivateResources: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.AllowedToInactivateResources"),
			config: {
				predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptionsWithoutZeroOption<UserType>(UserType) },
				multiSelect: true
			},
		}),
		allowedToDeleteResources: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.AllowedToDeleteResources"),
			config: {
				predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptionsWithoutZeroOption<UserType>(UserType) },
				multiSelect: true
			},
		}),
		resourceOverviewProperties: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourceOverviewProperties"),
			config: {
				component: EntityFieldSelector,
				schema: () => resourceSchema,
				customProperties: () => settings().resourceCustomProperties,
				multiSelect: true,
				propertyTypes: 'both',
			}
		}),
		projectPickerDisplayFields: fieldOptionsFactory.formItem({
			displayName: translate.t("Settings.ProjectPickerDisplayFields"),
			config: {
				schema: {
					schema: pickerFieldsSchema('project')
				}
			},
			hideLabel: false
		}),
		allowedToInactivateProjects: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.AllowedToInactivateProjects"),
			config: {
				predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptionsWithoutZeroOption<UserType>(UserType) },
				multiSelect: true
			},
		}),
		projectInformationProperties: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ProjectInformationProperties"),
			config: {
				component: EntityFieldSelector,
				schema: () => projectSchema,
				customProperties: () => settings().projectCustomProperties,
				multiSelect: true,
				propertyTypes: 'both',
			}
		}),
		projectFilterPM: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ProjectFilterPM"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().projectCustomProperties,
				entityType: 'project',
				schema: () => projectSchema,
			}
		}),
		projectFilterLM: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ProjectFilterLM"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().projectCustomProperties,
				entityType: 'project',
				schema: () => projectSchema,
			}
		}),
		projectFilterOverview: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ProjectFilterOverview"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().projectCustomProperties,
				entityType: 'project',
				schema: () => projectSchema,
			}
		}),
		projectsAlwaysShowRBS: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.ProjectsAlwaysShowRBS"),
		}),
		projectOverviewProperties: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ProjectOverviewProperties"),
			config: {
				component: EntityFieldSelector,
				schema: () => projectSchema,
				customProperties: () => settings().projectCustomProperties,
				multiSelect: true,
				propertyTypes: 'both',
			}
		}),
		contractDefaultName: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ContractDefaultName"),
			config: {
				component: EntityFieldSelector,
				schema: () => resourceSchema,
				customProperties: () => settings().resourceCustomProperties,
				propertyTypes: 'both',
			}
		}),
		allowLMsToChangeContractName: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.AllowLMsToChangeContractName"),
		}),
		freezePeriod: fieldOptionsFactory.formItem({
			displayName: translate.t("Settings.FreezePeriod"),
			config: {
				schema: {
					schema: freezePeriodSchema()
				}
			},
			hideLabel: false
		}),
		autoGroupContractBy: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.AutoGroupContractBy"),
			config: {
				component: EntityFieldSelector,
				schema: () => resourceSchema,
				customProperties: () => settings().resourceCustomProperties,
				propertyTypes: 'both',
			}
		}),
		singletonOnRequests: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.SingletonOnRequests"),
		}),
		enableDynamicContractNames: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableDynamicContractNames"),
		}),
		enableGridResolutionMismatchWarning: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableGridResolutionMismatchWarning"),
		}),
		mailNotifications: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.MailNotifications"),
			config: {
				multiSelect: true,
				predefinedOptions: {
					optionsAsync: async _ => {
						const dto = {
							entity: EntityType.ContentConfiguration,
							query: { expression: 'ContentConfigurationType == @0', params: [ContentConfigurationType.MailTemplate] } as DynamicQuery,
						} as QueryDto;
						const response = await ApiCalls.queryEntity(dto);
						return DropdownOptionEx.toDropdownOptions<MailTemplate>(response.data, _ => _.id, _ => _.name);
					} 
				}
			}
		}),
		timelineDayResolutionMaxDaysBackwards: fieldOptionsFactory.number({
			displayName: translate.t("Settings.TimelineDayResolutionMaxDaysBackwards"),
		}),
		timelineDayResolutionMaxDaysForward: fieldOptionsFactory.number({
			displayName: translate.t("Settings.TimelineDayResolutionMaxDaysForward"),
		}),
		localization: fieldOptionsFactory.string({
			displayName: translate.t("Settings.Localization"),
		}),
		genericResourceDefinition: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.GenericResourceDefinition"),
			config: {
				component: EntityFieldSelector,
				schema: () => resourceSchema,
				customProperties: () => settings().resourceCustomProperties,
				multiSelect: true,
				propertyTypes: 'both',
			}
		}),
		autoGenerateGenericResources: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.AutoGenerateGenericResources"),
		}),
		genericResourceNamingConvention: fieldOptionsFactory.string({
			displayName: translate.t("Settings.GenericResourceNamingConvention"),
		}),
		lightbulbLimitInMonths: fieldOptionsFactory.number({
			displayName: translate.t("Settings.LightbulbLimitInMonths"),
		}),
		unitType: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.UnitType"),
			config: {
				predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptions<UnitType>(UnitType) }
			},
		}),
		defaultTheme: fieldOptionsFactory.predefinedArray({
			displayName: translate.t("Settings.DefaultTheme"),
			config: {
				multiSelect: true,
				predefinedOptions: {
					optionsAsync: async _ => {
						const dto = {
							entity: EntityType.ContentConfiguration,
							query: { expression: 'ContentConfigurationType == @0', params: [ContentConfigurationType.Theme] } as DynamicQuery,
						} as QueryDto;
						const response = await ApiCalls.queryEntity(dto);
						return DropdownOptionEx.toDropdownOptions<any>(response.data, _ => _.id, _ => _.name);
					} 
				}
			}
		}),
		enableProjectExportRefresh: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableProjectExportRefresh"),
		}),
		restrictOverAllocating: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.RestrictOverAllocating"),
		}),
		enableHelpSection: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableHelpSection"),
		}),
		resourcesCanEditOwnAllocations: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourcesCanEditOwnAllocations"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().projectCustomProperties,
				entityType: 'project',
				schema: () => projectSchema,
			}
		}),
		restrictDirectAllocations: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.RestrictDirectAllocations"),
		}),
		resourcesCanAddAllocations: fieldOptionsFactory.customComponent({
			displayName: translate.t("Settings.ResourcesCanAddAllocations"),
			config: {
				component: EntityFieldFilterEditor,
				customProperties: () => settings().projectCustomProperties,
				entityType: 'project',
				schema: () => projectSchema,
			}
		}),
		enableProjectPlanner: fieldOptionsFactory.boolean({
			displayName: 'Enable Project Planner view',
		}),
		enableResourcePlanner: fieldOptionsFactory.boolean({
			displayName: 'Enable Resource Planner view',
		}),
		enableFastDelete: fieldOptionsFactory.boolean({
			displayName: translate.t("Settings.EnableFastDelete"),
		}),
	}
}
